// Секцията на началния екран на „Прехвърляне": прехвърлените днес от всички
// складове (час · посока · продукти · въвел · бруто). Глупав рендер — данните
// идват от страницата; клик на ред отваря документа. Редовете са плътни: ЕДИН
// ред, вторичното приглушено след посоката.
import dayjs from "dayjs";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { TransferDocument } from "../../../../../api/inventoryTransfers";
import { formatMoney } from "../../../components/numbers";
import { SECONDARY_BUTTON_CLASS } from "../../../components/list/buttonStyles";
import { SectionCard, SectionTitle } from "../../../components/list/ListBits";
import { ROW_DENSE } from "../../../components/list/density";
import { TruncatedText } from "../../../components/list/TruncatedText";
import { enteredAtOf, fromWarehouseOf, toWarehouseOf, transferUserOf } from "../transferMeta";

export const TodayTransfersSection = ({
  transfers,
  onView,
  onNew,
}: {
  transfers: TransferDocument[];
  onView: (transfer: TransferDocument) => void;
  onNew: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle
        title={t("inventoryPage.trxHomeTodayTitle")}
        hint={t("inventoryPage.trxHomeTodayCount", { count: transfers.length })}
      />
      {transfers.length === 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-shade-4 px-[18px] py-3.5 text-muted">
          <span>{t("inventoryPage.trxHomeTodayNone")}</span>
          <button type="button" onClick={onNew} className={SECONDARY_BUTTON_CLASS}>
            {t("inventoryPage.newTransferPlus")}
          </button>
        </div>
      ) : (
        <SectionCard>
          {transfers.map((transfer, index) => {
            const from = fromWarehouseOf(transfer);
            const to = toWarehouseOf(transfer);
            const meta = [
              `№${transfer.id}`,
              t("inventoryPage.homeDraftLines", { count: transfer.productsCount ?? 0 }),
              transferUserOf(transfer),
              transfer.note || null,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <button
                key={transfer.id}
                type="button"
                onClick={() => onView(transfer)}
                className={`flex w-full cursor-pointer items-center gap-3 border-none bg-transparent px-3.5 text-left text-ink outline-none hover:bg-fill focus-visible:bg-fill ${ROW_DENSE} ${
                  index ? "border-t border-solid border-shade-1" : ""
                }`}
              >
                <span className="w-10 shrink-0 font-sheet text-[12.5px] text-body">
                  {dayjs(enteredAtOf(transfer)).format("HH:mm")}
                </span>
                <TruncatedText text={`${from} → ${to} · ${meta}`} className="flex-1">
                  <span className="font-medium">
                    {from}
                    <ArrowRightOutlined className="mx-1.5 text-[11px] text-brand-deep-ink" />
                    {to}
                  </span>
                  <span className="text-[12.5px] text-body"> · {meta}</span>
                </TruncatedText>
                <span className="shrink-0 font-sheet font-semibold">
                  {formatMoney(transfer.totalValueWithVat || 0)}
                </span>
              </button>
            );
          })}
        </SectionCard>
      )}
    </section>
  );
};
